import cardValidator from 'card-validator';
import isAfter from 'date-fns/isAfter';
import isDate from 'date-fns/isDate';
import parseISO from 'date-fns/parseISO';

export const FORMAT_VALIDATORS = {
  'credit-card-number': validateCreditCardNumber,
  'account-number': validateAccountNumber,
  email: validateEmail,
  'optional-email': validateEmail,
  date: validateDate,
  'date-not-future': validateDateNotFuture,
  'date-time': validateDate,
  'date-time-not-future': validateDateNotFuture,
  'phone-number': validatePhoneNumber,
  nin: validateNin,
  'organization-number': validateOrganizationNumber,
  'license-plate-number': validateLicensePlateNumber,
  'birth-date': validateDateOfBirth
};

export const FORMAT_SANITIZERS: {
  [key in ValidationStringFormat]?: (value: string) => string;
} = {
  'credit-card-number': removeNonNumeric,
  'account-number': removeNonNumeric
};

export type ValidationStringFormat = keyof typeof FORMAT_VALIDATORS;

function mod11(value: string, weightNumber: number) {
  let sum = 0;
  for (let i = 0; i < value.length - 1; i++) {
    sum += parseInt(value.charAt(i), 10) * (7 - ((i + weightNumber) % 6));
  }
  const remainder = sum % 11;
  return remainder === 0 ? 0 : 11 - remainder;
}

export function validateOrganizationNumber(value: string | null | undefined) {
  if (!value) {
    return false;
  }

  if (startsOrEndsWithSpace(value)) {
    return false;
  }

  const organizationNumber = value.toString().replace(/[.\s]/g, '');
  if (organizationNumber.length !== 9) {
    return false;
  }

  const controlDigit = parseInt(
    organizationNumber.charAt(organizationNumber.length - 1),
    10
  );

  return controlDigit === mod11(organizationNumber, 4);
}

function startsOrEndsWithSpace(value: string) {
  return /^\s|\s$/.test(value);
}

export function validateAccountNumber(value: string | null | undefined) {
  if (!value) {
    return false;
  }

  const accountNumber = value.toString().replace(/[.\s]/g, '');
  if (accountNumber.length !== 11) {
    return false;
  }

  const controlDigit = parseInt(
    accountNumber.charAt(accountNumber.length - 1),
    10
  );

  return controlDigit === mod11(accountNumber, 2);
}

export function validateCreditCardNumber(value: string) {
  return cardValidator.number(value).isValid;
}

export function validateEmail(value: string | null | undefined) {
  if (!value) {
    return false;
  }

  // https://github.com/scottgonzalez/sane-email-validation
  // MIT
  const localAddr = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+$/i;
  const domain =
    /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;

  const parts = value.split('@');

  if (parts.length !== 2) {
    return false;
  }

  if (!localAddr.test(parts[0])) {
    return false;
  }

  if (!domain.test(parts[1])) {
    return false;
  }

  if (parts[0].substr(-1) === '.') {
    return false;
  }

  return true;
}

export function validateDate(value: string | null | undefined) {
  if (!value) {
    return false;
  }

  const parsed = parseISO(value);

  // Invalid Date is a Date
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }

  return isDate(parsed);
}

export function validateDateNotFuture(
  value: string | null | undefined,
  now = new Date()
) {
  if (!validateDate(value)) {
    return false;
  }

  return !isAfter(parseISO(value!), now);
}

export function validatePhoneNumber(value: string | null | undefined) {
  if (!value) {
    return false;
  }

  return /^(\+)?(\d{4,20})$/.test(value.replace(/\s+/g, ''));
}

export function validateNin(value: string) {
  if (!value || value.length !== 11) {
    return false;
  }

  const weightedSum = (val: string, factors: Array<number>) => {
    let sum = 0;
    for (let i = 0; i < factors.length; ++i) {
      sum += parseInt(val.charAt(i), 10) * factors[i];
    }
    return sum;
  };

  let checksum1 = 11 - (weightedSum(value, [3, 7, 6, 1, 8, 9, 4, 5, 2]) % 11);
  if (checksum1 === 11) {
    checksum1 = 0;
  }

  let checksum2 =
    11 - (weightedSum(value, [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]) % 11);
  if (checksum2 === 11) {
    checksum2 = 0;
  }

  return (
    checksum1 === parseInt(value.charAt(9), 10) &&
    checksum2 === parseInt(value.charAt(10), 10)
  );
}

function removeNonNumeric(value: string | null): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/\D+/g, '');
}

export function validateLicensePlateNumber(value: string | null | undefined) {
  if (!value) {
    return false;
  }

  return /^[a-zA-Z0-9]*$/.test(value);
}

export function validateDateOfBirth(value: string | null | undefined) {
  if (!value) {
    return false;
  }

  return /^(?:0[1-9]|[12]\d|3[01])(?:0[1-9]|1[012])(\d\d)$/.test(value);
}
